'use client'
import Image from 'next/image'
import { tw } from 'utils/classnames'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { SkeletonItem } from 'components/skeleton-item'
import { isMobileUserAgent } from '@repo/web-utils/is-mobile-user-agent'

import { useClubhousesListMembersQuery } from '@web/gen/clubhouses'

export function UserAvatar({
  slug,
  size = 'default',
  displayCount,
}: {
  slug: string
  size?: 'small' | 'default'
  displayCount?: number
}) {
  const { data, isPending } = useClubhousesListMembersQuery({
    input: { clubhouseSlug: slug, itemsPerPage: 20, page: 1 },
  })

  if (isPending) {
    return <Skeleton size={size} displayCount={displayCount} />
  }

  const members = data?.clubhousesListMembers?.members || []

  const isMobile = isMobileUserAgent()
  const maxMobileVisible = 12 // Maximum number of avatars to show on mobile

  let visibleMembers = members
  let overflowCount = 0

  if (isMobile) {
    if (members.length > maxMobileVisible) {
      visibleMembers = members.slice(0, maxMobileVisible)
      overflowCount = members.length - maxMobileVisible
    }
  } else if (displayCount) {
    visibleMembers = members.slice(0, displayCount)
  }

  const avatarSize = size === 'small' ? 'size-4 -ml-3' : 'size-8 -ml-4'

  return (
    <div className="flex flex-row gap-2">
      {visibleMembers.map((member, index) =>
        member.avatarUrl ? (
          <Link
            href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${member.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            key={member.id}
            className={tw(
              `bg-cf-white hover:shadow-cf-elevation-light-1 outline-cf-white hover:outline-cf-white ${avatarSize} overflow-hidden rounded-full outline outline-offset-0 hover:isolate hover:outline-4`,
              index === 0 && 'ml-0',
            )}
            data-testid="user-avatar"
          >
            <Image src={member.avatarUrl} alt="User Avatar" width={32} height={32} />
          </Link>
        ) : null,
      )}
      {isMobile && overflowCount > 0 ? (
        <Link
          href={`${AppRoutes.CLUBHOUSE}${slug}/members/`}
          className={tw(
            `bg-cf-purple-900 text-cf-white text-14 flex items-center justify-center font-medium ${avatarSize} hover:outline-cf-white outline-cf-white overflow-hidden rounded-full outline outline-offset-0 hover:isolate hover:outline-4`,
            visibleMembers.length === 0 && 'ml-0',
          )}
        >
          +{overflowCount}
        </Link>
      ) : null}
    </div>
  )
}

function Skeleton({
  size = 'default',
  displayCount,
}: {
  size?: 'small' | 'default'
  displayCount?: number
}) {
  const count = displayCount ?? 10
  const skeletonSize = size === 'small' ? 'size-4 -ml-3' : 'size-8 -ml-4 '

  return (
    <div className="flex flex-row gap-2">
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonItem
          key={index}
          className={tw(
            `${skeletonSize}`,
            `overflow-hidden rounded-full outline outline-offset-0`,
            index === 0 && 'ml-0',
          )}
        />
      ))}
    </div>
  )
}
